/**
 * PublicKnowledgeBaseService
 *
 * Anonymous-capable Knowledge Base surface consumed by ccms_resource_centre.
 * The backend filters by status + visibility + audience (see the
 * PublicKnowledgeBaseController + VisibilityResolutionService); this SDK
 * service just shapes the HTTP calls. Mirrors the public-courses pattern.
 *
 * Routes (under the `resources` microservice):
 *   GET /api/public/knowledge-bases
 *   GET /api/public/knowledge-bases/{id}
 *   GET /api/public/knowledge-bases/{id}/categories
 *   GET /api/public/kb-categories/{id}
 *   GET /api/public/kb-categories/{id}/articles
 *   GET /api/public/kb-articles/{slug}
 *   GET /api/public/kb-articles/{id}/steps
 *   GET /api/public/kb-articles/{id}/steps/media
 */
import { BaseService } from '../base/BaseService';
/**
 * Returns enriched <c>*ModelView</c> shapes (visibility + status name/color
 * filled in by <c>PublicKnowledgeBaseEnrichedComposer</c>) so the Resource
 * Centre can render the <c>VisibilityBadge</c> in one round-trip. The
 * backend filters by status + visibility + audience before composing.
 */
export class PublicKnowledgeBaseService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? PublicKnowledgeBaseService.DEFAULT_SERVICE_PATH,
            basePath: PublicKnowledgeBaseService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ─── KnowledgeBase ────────────────────────────────────────────────────
    /** List the knowledge bases the caller may see. */
    async listKnowledgeBases() {
        const response = await this.listKnowledgeBasesRaw();
        return response.data ?? [];
    }
    listKnowledgeBasesRaw() {
        return this.http.get(this.buildUrl('knowledge-bases'));
    }
    /** Get one KB by id. Unlisted KBs resolve by direct link. */
    async getKnowledgeBase(knowledgeBaseId) {
        const response = await this.getKnowledgeBaseRaw(knowledgeBaseId);
        return response.data ?? null;
    }
    getKnowledgeBaseRaw(knowledgeBaseId) {
        return this.http.get(this.buildUrl(`knowledge-bases/${knowledgeBaseId}`));
    }
    /** List the categories under a KB the caller may see. */
    async listCategoriesForKnowledgeBase(knowledgeBaseId) {
        const response = await this.listCategoriesForKnowledgeBaseRaw(knowledgeBaseId);
        return response.data ?? [];
    }
    listCategoriesForKnowledgeBaseRaw(knowledgeBaseId) {
        return this.http.get(this.buildUrl(`knowledge-bases/${knowledgeBaseId}/categories`));
    }
    // ─── KBCategory ───────────────────────────────────────────────────────
    /** Get one category. Walks the parent chain server-side to enforce visibility. */
    async getCategory(kbCategoryId) {
        const response = await this.getCategoryRaw(kbCategoryId);
        return response.data ?? null;
    }
    getCategoryRaw(kbCategoryId) {
        return this.http.get(this.buildUrl(`kb-categories/${kbCategoryId}`));
    }
    /** List the articles in a category that the caller may see. */
    async listArticlesInCategory(kbCategoryId) {
        const response = await this.listArticlesInCategoryRaw(kbCategoryId);
        return response.data ?? [];
    }
    listArticlesInCategoryRaw(kbCategoryId) {
        return this.http.get(this.buildUrl(`kb-categories/${kbCategoryId}/articles`));
    }
    // ─── KBArticle ────────────────────────────────────────────────────────
    /** Get an article by slug. Walks the full KB → Category → Article chain. */
    async getArticleBySlug(slug) {
        const response = await this.getArticleBySlugRaw(slug);
        return response.data ?? null;
    }
    getArticleBySlugRaw(slug) {
        return this.http.get(this.buildUrl(`kb-articles/${encodeURIComponent(slug)}`));
    }
    /** List the visible steps of an article (by id). */
    async listArticleSteps(kbArticleId) {
        const response = await this.listArticleStepsRaw(kbArticleId);
        return response.data ?? [];
    }
    listArticleStepsRaw(kbArticleId) {
        return this.http.get(this.buildUrl(`kb-articles/${kbArticleId}/steps`));
    }
    /**
     * Streaming media (HLS, transcripts, audio) for the visible steps of an
     * article that have linked media. Steps without media are omitted.
     */
    async getArticleStepsMedia(kbArticleId) {
        const response = await this.getArticleStepsMediaRaw(kbArticleId);
        return response.data ?? [];
    }
    getArticleStepsMediaRaw(kbArticleId) {
        return this.http.get(this.buildUrl(`kb-articles/${kbArticleId}/steps/media`));
    }
}
PublicKnowledgeBaseService.DEFAULT_SERVICE_PATH = 'resources';
// `api/public` so we can route between `knowledge-bases`, `kb-categories`
// and `kb-articles` from a single service.
PublicKnowledgeBaseService.BASE_PATH = 'api/public';
//# sourceMappingURL=PublicKnowledgeBaseService.js.map